

'use client';

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "../theme-toggle";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useBookDemo } from "@/context/schedule-demo-context";
import { usePathname } from "next/navigation";
import { isCustomerPage } from "@/lib/customer-pages";
import { Badge } from "../ui/badge";

export default function Header() {
  // Scrolls to this page's contact section, or goes to /contact when there is none.
  const customer = isCustomerPage(usePathname());
  const handleBookDemoClick = useBookDemo();

  return (
    <header 
      className="px-4 lg:px-6 h-16 flex items-center sticky z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-[top] duration-500 ease-in-out"
      style={{ top: 'var(--announcement-bar-height, 0px)' }}
    >
      <div className="container mx-auto flex items-center justify-between">
        <Link href="/" className="flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          <Logo />
        </Link>
        <nav className="hidden lg:flex gap-4 sm:gap-6 items-center">
          <Link
            href="/#features"
            className="text-sm font-medium hover:underline underline-offset-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            How It Works
          </Link>
          <Link
            href="/platform"
            className="text-sm font-medium hover:underline underline-offset-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Platform
          </Link>
          <Link
            href="/#impact"
            className="text-sm font-medium hover:underline underline-offset-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Business Impact
          </Link>
          <Link
            href="/audit-transparency"
            className="text-sm font-medium hover:underline underline-offset-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Audit Module
          </Link>
          <Link
            href="/em-coding"
            className="text-sm font-medium hover:underline underline-offset-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex items-center gap-1.5"
          >
            E/M Coding
            <Badge variant="default" className="text-xs px-1.5 py-0.5 leading-none">New</Badge>
          </Link>
          <Link
            href="/blog"
            className="text-sm font-medium hover:underline underline-offset-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Blog
          </Link>
          <button
            onClick={handleBookDemoClick}
            className="text-sm font-medium hover:underline underline-offset-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Contact
          </button>
        </nav>
        <div className="flex items-center gap-2">
          <div className="hidden lg:block">
            {/* Customer pages already have Contact in the nav; no sales button there. */}
            {!customer && <Button onClick={handleBookDemoClick}>Book Your Personalized Demo</Button>}
          </div>
          <ThemeToggle />
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle navigation menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right">
                <SheetHeader className="sr-only">
                  <SheetTitle>Navigation Menu</SheetTitle>
                  <SheetDescription>
                    A list of navigation links to sections on the page.
                  </SheetDescription>
                </SheetHeader>
                <nav className="grid gap-6 text-lg font-medium mt-6">
                  <Link
                    href="/"
                    className="flex items-center gap-2 text-lg font-semibold mb-4"
                  >
                    <Logo />
                  </Link>
                  <Link
                    href="/#features"
                    className="hover:underline underline-offset-4"
                  >
                    How It Works
                  </Link>
                  <Link
                    href="/platform"
                    className="hover:underline underline-offset-4"
                  >
                    Platform
                  </Link>
                  <Link
                    href="/#impact"
                    className="hover:underline underline-offset-4"
                  >
                    Business Impact
                  </Link>
                   <Link
                    href="/audit-transparency"
                    className="hover:underline underline-offset-4"
                  >
                    Audit Module
                  </Link>
                  <Link
                    href="/em-coding"
                    className="hover:underline underline-offset-4 flex items-center gap-2"
                  >
                    E/M Coding
                    <Badge variant="default" className="text-xs px-1.5 py-0.5 leading-none">New</Badge>
                  </Link>
                   <Link
                    href="/blog"
                    className="hover:underline underline-offset-4"
                  >
                    Blog
                  </Link>
                  <button
                    onClick={handleBookDemoClick}
                    className="text-left hover:underline underline-offset-4"
                  >
                    Contact
                  </button>
                  {!customer && (
                    <div className="pt-6">
                      <Button className="w-full" onClick={handleBookDemoClick}>
                        Book Your Personalized Demo
                      </Button>
                    </div>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
